// Sign-in screen — email + password only. Google OAuth and magic link have
// been removed. Invitees receive a Supabase invite email (sent server-side
// from the Cloudflare Worker /invite route) which sets their password on
// first click; thereafter everyone uses email + password to sign in.

import { useState } from 'react';
import { supabase, supabaseConfigured } from '../config/supabaseClient';
import { useAuth } from './AuthContext';

const REDIRECT = () => window.location.origin + window.location.pathname;

// Shown when a sign-in succeeds but the email isn't on the tree (and isn't
// an admin). The auth context has already signed them out — this just
// explains why and offers a way to try a different email.
export function NotInvitedPage({ email }) {
  const { signOut } = useAuth();
  return (
    <div className="login-page">
      <div className="login-card">
        <h1>Gia Phả</h1>
        <p className="login-tagline">This email isn't on the family tree</p>
        <p className="login-error" style={{ marginTop: 16 }}>
          {email ? <>The address <strong>{email}</strong> hasn't been invited to this tree yet.</> : null}
          {' '}Ask the family administrator to add you to the tree with your
          email, then sign in again.
        </p>
        <button
          type="button"
          className="login-magic-btn"
          style={{ marginTop: 18 }}
          onClick={signOut}
        >
          Try a different email
        </button>
      </div>
    </div>
  );
}

// PASSWORD_RECOVERY landing — Supabase fires this event when a user clicks
// a password-reset or invite email link. Force them to set a new password
// before continuing.
export function SetPasswordPage({ heading, subtitle, onDone }) {
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (pw1.length < 8) {
      setErr('Password must be at least 8 characters.');
      return;
    }
    if (pw1 !== pw2) {
      setErr('Passwords do not match.');
      return;
    }
    setBusy(true);
    setErr(null);
    const { error } = await supabase.auth.updateUser({ password: pw1 });
    setBusy(false);
    if (error) {
      setErr(error.message);
      return;
    }
    onDone?.();
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>Gia Phả</h1>
        <p className="login-tagline">{subtitle}</p>
        <form onSubmit={submit} className="login-magic-form">
          <label className="login-field">
            <span>{heading || 'New password'}</span>
            <input
              type="password"
              value={pw1}
              onChange={(e) => { setPw1(e.target.value); setErr(null); }}
              autoFocus
              required
              minLength={8}
              autoComplete="new-password"
              disabled={busy}
            />
          </label>
          <label className="login-field">
            <span>Confirm password</span>
            <input
              type="password"
              value={pw2}
              onChange={(e) => { setPw2(e.target.value); setErr(null); }}
              required
              minLength={8}
              autoComplete="new-password"
              disabled={busy}
            />
          </label>
          <button
            type="submit"
            className="login-magic-btn"
            disabled={busy || !pw1 || !pw2}
          >
            {busy ? 'Saving…' : 'Save password'}
          </button>
        </form>
        {err && <p className="login-error">{err}</p>}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [resetMsg, setResetMsg] = useState(null);
  const [mode, setMode] = useState('signin'); // 'signin' | 'forgot'

  if (!supabaseConfigured) {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Gia Phả</h1>
          <p className="login-error">
            Supabase is not configured. Set REACT_APP_SUPABASE_URL and
            REACT_APP_SUPABASE_ANON_KEY in <code>.env.local</code>, then
            restart the dev server.
          </p>
        </div>
      </div>
    );
  }

  const onSignIn = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || !password) return;
    setBusy(true);
    setErr(null);
    setResetMsg(null);
    const { error } = await supabase.auth.signInWithPassword({
      email: trimmed,
      password,
    });
    setBusy(false);
    if (error) {
      setErr(error.message);
    }
    // On success, AuthContext picks up the SIGNED_IN event and the gate
    // re-renders into the tree.
  };

  const onForgot = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setBusy(true);
    setErr(null);
    setResetMsg(null);
    const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
      redirectTo: REDIRECT(),
    });
    setBusy(false);
    if (error) {
      setErr(error.message);
    } else {
      setResetMsg(
        `Check your email — we sent a reset link to ${trimmed}.` +
          ' Click the link to set a new password.'
      );
    }
  };

  if (mode === 'forgot') {
    return (
      <div className="login-page">
        <div className="login-card">
          <h1>Gia Phả</h1>
          <p className="login-tagline">Reset your password</p>
          <form onSubmit={onForgot} className="login-magic-form">
            <label className="login-field">
              <span>Email</span>
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErr(null); setResetMsg(null); }}
                placeholder="you@example.com"
                autoComplete="email"
                required
                autoFocus
                disabled={busy}
              />
            </label>
            <button
              type="submit"
              className="login-magic-btn"
              disabled={busy || !email.trim()}
            >
              {busy ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
          {resetMsg && <p className="login-msg">{resetMsg}</p>}
          {err && <p className="login-error">{err}</p>}
          <button
            type="button"
            className="login-link-btn"
            onClick={() => { setMode('signin'); setErr(null); setResetMsg(null); }}
          >
            ← Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page">
      <div className="login-card">
        <h1>Gia Phả</h1>
        <p className="login-tagline">Sign in to view the family tree</p>
        <form onSubmit={onSignIn} className="login-magic-form">
          <label className="login-field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErr(null); }}
              placeholder="you@example.com"
              autoComplete="email"
              required
              autoFocus
              disabled={busy}
            />
          </label>
          <label className="login-field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setErr(null); }}
              autoComplete="current-password"
              required
              disabled={busy}
            />
          </label>
          <button
            type="submit"
            className="login-magic-btn"
            disabled={busy || !email.trim() || !password}
          >
            {busy ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        {err && <p className="login-error">{err}</p>}
        <button
          type="button"
          className="login-link-btn"
          onClick={() => { setMode('forgot'); setErr(null); setResetMsg(null); }}
        >
          Forgot password?
        </button>
      </div>
    </div>
  );
}
